import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IPaginateRequest, IQuizYear, IUserCourse, IUserInterview, IUserQuestionAnswer, IUserQuiz } from "utils/types";
import { Loader } from "common";
import { ProgressCard, ProgressCardProps } from "./ProgressCard";
import { IGetQuizQuestions, useQuizStore } from "store";

const Container = styled.div`
  padding: 20px 40px;
  font-family: "Manrope", sans-serif;
  width: 100%;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #eee;
  margin-bottom: 30px;
`;

const Tab = styled.div<{ active: boolean }>`
  padding: 10px 15px;
  cursor: pointer;
  color: ${({ active }) => (active ? "#5575f7" : "#6c757d")};
  border-bottom: ${({ active }) => (active ? "2px solid #5575f7" : "none")};
  font-weight: ${({ active }) => (active ? "500" : "normal")};
  margin-right: 20px;
`;

const QuizList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 700px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  color: #6c757d;
`;


type IFilter = "inProgress"|"completed"|"all"
export interface ProgressProps extends Pick<ProgressCardProps, 
  "onGetCourse"|"onContinueCourse"|"onGetCourseModuleCount"|"onGetInterview"|"onRetakeInterview">{ 
    onGetUserInterviews: (data: {userInterview: Partial<IUserInterview>} & IPaginateRequest) => Promise<IUserInterview[]>;
    onGetUserQuizzes: (data: {userQuiz?: Partial<IUserQuiz>} & IPaginateRequest) => Promise<IUserQuiz[]>;
    onGetUserCourses: (data: { userCourse?: Partial<IUserCourse>} & IPaginateRequest) => Promise<IUserCourse[]>;
    onGetQuizYears: (courseId: string) => Promise<IQuizYear[]>;
    onGetQuizQuestions: IGetQuizQuestions;
    onGetUserQuestionAnswers: (quizId: string) => Promise<IUserQuestionAnswer[]>
}
export const Progress: React.FC<ProgressProps> = ({
  onGetUserQuizzes, onGetUserQuestionAnswers, onContinueCourse,
   onGetCourse, onGetUserCourses, onGetCourseModuleCount,
   onGetQuizQuestions, onGetQuizYears, onGetUserInterviews,
   onGetInterview, onRetakeInterview
}) => {
  const [activeTab, setActiveTab] = useState<IFilter>("inProgress");
  const defaultLimit = 50;
  const setTab = (tab: IFilter) => {
    setActiveTab(tab);
    setPagination({ limit: defaultLimit, skip: 0 })
  }
  const [userCourses, setUserCourses] = useState<IUserCourse[]>([])
  const [userQuizzes, setUserQuizzes] = useState<IUserQuiz[]>([])
  const [userInterviews, setUserInterviews] = useState<IUserInterview[]>([])
  const [pagination, setPagination] = useState<IPaginateRequest>({ limit: 100, skip: 0 })
  const [loading, setLoading] = useState(false)
  const { loadQuiz, loadReport } = useQuizStore()


  useEffect(() => {
    (async () => {    
      setLoading(true);  
      switch(activeTab){
        case "all": 
          const [userCourses, userQuizzes, userInterviews] = await Promise.all([
            onGetUserCourses({ ...pagination }),
            onGetUserQuizzes({ userQuiz: { completed: "true" }, ...pagination}),
            onGetUserInterviews({ userInterview: {}, ...pagination})
          ]);
          setUserCourses(userCourses);
          setUserQuizzes(userQuizzes);
          setUserInterviews(userInterviews)
          break;
        
        case "inProgress": {
          const userCourses = await onGetUserCourses({ userCourse: { completed: "false"}, ...pagination});
          setUserCourses(userCourses)
          setUserQuizzes([])
          break;
        }
        case "completed": {
          const [userCourses, userQuizzes, userInterviews] = await Promise.all([
            onGetUserCourses({ userCourse: { completed: "true"}, ...pagination}),
            onGetUserQuizzes({ userQuiz: { completed: "true" }, ...pagination}),
            onGetUserInterviews({ userInterview: { completed: "true" }, ...pagination})
          ])
          setUserCourses(userCourses);
          setUserQuizzes(userQuizzes)
          setUserInterviews(userInterviews)
          break;
        }
      }
      setLoading(false)
    })();
  }, [activeTab, JSON.stringify(pagination)])

  if(loading){
    return <Loader/>
  }

  // userQuizzes and unFinishedQuizzes and userQuizzes together
  const quizzes = [
    ...userQuizzes.map(item => ({...item, type: "quiz"})), 
    ...userCourses.map(item => ({...item, type: "course"})),
    ...userInterviews.map(item => ({...item, type: "interview"}))
  ]
  // sort by createdAt
  quizzes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  
  return (
    <Container>
      <TabsContainer>
        {/* <Tab
          active={activeTab === "all"}
          onClick={() => setTab("all")}
        >
          All
        </Tab> */}
        <Tab
          active={activeTab === "inProgress"}
          onClick={() => setTab("inProgress")}
        >
          In-progress
        </Tab>
        <Tab
          active={activeTab === "completed"}
          onClick={() => setTab("completed")}
        >
          Completed
        </Tab>
      </TabsContainer>
      {quizzes.length === 0 ? 
        <EmptyState>
          No quizzes found.
        </EmptyState> :
        <QuizList>
          {quizzes.map((quiz, index) => {
            let courseId = (quiz as IUserCourse).courseId || ""
            if(quiz.type === "quiz"){
              courseId = (quiz as IUserQuiz).quiz?.courseId || ""
            }else if(quiz.type === "interview"){
              courseId = (quiz as IUserInterview).interviewId
            }
            return (
              <ProgressCard key={`quiz-${index}-${quiz.id}`} {...{
                courseId,
                data: quiz,
                onGetCourse,
                onContinueCourse,
                onGetCourseModuleCount,
                onGetInterview,
                onRetakeInterview,
                onTakeQuiz(courseId, quizId) {
                  loadQuiz({
                    quizId,
                    loadAction: () => onGetQuizYears(courseId),
                    onGetQuizQuestions
                  })
                },
                onViewQuizReport(userQuiz) {
                  loadReport({
                    userQuiz,
                    onGetQuizQuestions,
                    loadAction: async () => {
                      if(!userQuiz.quiz){
                        return {}
                      }
                      const quizYears = await onGetQuizYears(userQuiz.quiz.courseId);
                      const quizYear = quizYears.find(item => item.quizId === userQuiz.quizId);
                      const quizAnswers = await onGetUserQuestionAnswers(userQuiz.quizId); 
                      return {
                        quizYear, 
                        quizYears,
                        quizAnswers
                      }
                    }
                  })
                },
              }}/>
            )
          })}         
        </QuizList>}
    </Container>
  );
};
